import React, { useState } from "react";
import { MdRefresh, MdSearch } from "react-icons/md";
import { useTaskList } from "../store/useTaskList";
import { CardSkeleton, TableSkeleton } from "@/components/ui/Skeleton";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/Empty";
import { StatusBadge } from "@/components/ui/StatusBadge";

const TaskListPage: React.FC = () => {
  const { tasks, total, loading, error, refetch } = useTaskList();
  const [search, setSearch] = useState("");

  const filtered = tasks.filter(
    (t) =>
      t.taskId.toLowerCase().includes(search.toLowerCase()) ||
      t.deviceName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <MdSearch
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by task ID or device name…"
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
          />
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <MdRefresh size={15} />
          Refresh
        </button>
        <span className="text-xs text-gray-400">
          {total} total task{total !== 1 ? "s" : ""}
        </span>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block">
        <Card>
          {loading ? (
            <TableSkeleton rows={6} cols={5} />
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No tasks found"
              description={
                search
                  ? "Try a different search term."
                  : "No analysis tasks are configured yet."
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="text-left pb-3 font-semibold">Task ID</th>
                    <th className="text-left pb-3 font-semibold">
                      Device Name
                    </th>
                    <th className="text-left pb-3 font-semibold">Status</th>
                    <th className="text-left pb-3 font-semibold">Abilities</th>
                    <th className="text-left pb-3 font-semibold">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((task) => (
                    <tr
                      key={task.taskId}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3  text-xs text-gray-600">
                        {task.taskId}
                      </td>
                      <td className="py-3 font-medium text-gray-800">
                        {task.deviceName}
                      </td>
                      <td className="py-3">
                        <StatusBadge status={task.status} />
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {task.abilities.map((a) => (
                            <span
                              key={a}
                              className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 text-xs text-red-400">
                        {task.errorReason || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
        ) : filtered.length === 0 ? (
          <EmptyState title="No tasks found" />
        ) : (
          filtered.map((task) => (
            <Card key={task.taskId}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {task.deviceName}
                  </p>
                  <p className="text-xs  text-gray-400">{task.taskId}</p>
                </div>
                <StatusBadge status={task.status} />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {task.abilities.map((a) => (
                  <span
                    key={a}
                    className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full"
                  >
                    {a}
                  </span>
                ))}
              </div>
              {task.errorReason && (
                <p className="text-xs text-red-400 mt-2">{task.errorReason}</p>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskListPage;
